import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BrowserRouter, useNavigate, useLocation } from "react-router-dom";
import { AlertTriangle, Bell, BriefcaseBusiness, Camera, CheckCircle2, ChevronRight, Clock3, CreditCard, FileText, Flame, Landmark, LogIn, LogOut, MapPin, Menu, MessageSquare, Plus, Search, Send, ShieldCheck, Smartphone, Sparkles, Star, Trash2, UserRound, Users, Wallet, X } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_NAME = "Dhruva IT Services";
const BRAND_LOGO = process.env.REACT_APP_BRAND_LOGO || "https://customer-assets-eiarnc6j.emergentagent.net/job_techconnect-42/artifacts/bbj890c6_file_00000000a9d882089bfaab79d63cef3b.png";

const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use(c => { const t = localStorage.getItem("taskflow_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const demoJobs = [];

function formatError(e) { const d=e?.response?.data?.detail; return Array.isArray(d)?d.map(x=>x.msg).join(" "):d||"Something went wrong. Please try again."; }
function formatINR(n) { const v = Number(n) || 0; return `₹${v.toLocaleString("en-IN")}`; }
function greeting() { const h = new Date().getHours(); if (h < 12) return "Good morning"; if (h < 17) return "Good afternoon"; return "Good evening"; }
function todayString() { return new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).toUpperCase(); }
function avatarUrl(path) { if (!path) return null; const t = localStorage.getItem("taskflow_token") || ""; return `${API}/files/avatar?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(t)}`; }

const I18N = {
  en: { overview:"Overview", findJobs:"Find jobs", myRequests:"My requests", myBookings:"My bookings", jobHistory:"Job history", paymentMethods:"Payment methods", profile:"Profile", help:"Help & FAQ", packages:"Support packages", technicians:"Nearby technicians", openRequests:"Open Requests", inProgress:"In Progress", closedTickets:"Closed Tickets", postNew:"Post a new job", welcome:"Good morning", chat:"Chat", send:"Send", typeMessage:"Type a message", viewAll:"View all", urgent:"URGENT — within 2 hours" },
  hi: { overview:"अवलोकन", findJobs:"नौकरियां खोजें", myRequests:"मेरे अनुरोध", myBookings:"मेरी बुकिंग", jobHistory:"जॉब इतिहास", paymentMethods:"भुगतान विधियां", profile:"प्रोफ़ाइल", help:"सहायता और FAQ", packages:"सपोर्ट पैकेज", technicians:"पास के तकनीशियन", openRequests:"खुले अनुरोध", inProgress:"चालू कार्य", closedTickets:"पूर्ण टिकट", postNew:"नया जॉब पोस्ट करें", welcome:"सुप्रभात", chat:"चैट", send:"भेजें", typeMessage:"संदेश लिखें…", viewAll:"सभी देखें", urgent:"अत्यावश्यक — 2 घंटे में" }
};

function App() { return <BrowserRouter><Marketplace /></BrowserRouter> }

function Marketplace() {
  const nav=useNavigate(), loc=useLocation();
  const [user,setUser]=useState(null);
  const [checking,setChecking]=useState(true);
  const [jobs,setJobs]=useState([]);
  const [active,setActive]=useState("dashboard");
  const [showAuth,setShowAuth]=useState(false);
  const [toast,setToast]=useState("");
  const [payFor,setPayFor]=useState(null);
  const [globalTerm,setGlobalTerm]=useState("");
  const [prefill,setPrefill]=useState(null);
  const [oauthBusy,setOauthBusy]=useState(false);
  const [chatJob,setChatJob]=useState(null);
  const [lang,setLang]=useState(()=>localStorage.getItem("dhruva_lang")||"en");
  useEffect(()=>{ localStorage.setItem("dhruva_lang", lang); },[lang]);
  const t = (k) => I18N[lang][k] || I18N.en[k] || k;

  // Detect Google OAuth callback in URL fragment synchronously (before /me check)
  const googleSessionId = useMemo(()=>{
    const h = loc.hash || "";
    const m = h.match(/session_id=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  },[loc.hash]);
  const hasOauth = !!googleSessionId;

  const resetToken = useMemo(()=>{
    if(loc.pathname!=="/reset-password") return null;
    const p = new URLSearchParams(loc.search);
    return p.get("token");
  },[loc.pathname, loc.search]);

  useEffect(()=>{
    if (hasOauth) return; // AuthCallback handles it
    api.get("/auth/me").then(r=>setUser(r.data)).catch(()=>{}).finally(()=>setChecking(false));
  },[hasOauth]);

  useEffect(()=>{
    if(!hasOauth) return;
    setOauthBusy(true);
    const pendingRole = localStorage.getItem("pending_google_role") || null;
    api.post("/auth/google/session", {session_id: googleSessionId, role: pendingRole})
      .then(r=>{
        localStorage.setItem("taskflow_token", r.data.access_token||"");
        localStorage.removeItem("pending_google_role");
        setUser(r.data);
        window.history.replaceState(null, "", "/");
      })
      .catch(e=>{ setToast(formatError(e)); window.history.replaceState(null, "", "/"); })
      .finally(()=>{ setOauthBusy(false); setChecking(false); });
  },[hasOauth, googleSessionId]);

  useEffect(()=>{ if(user) loadJobs(); },[user]);

  async function loadJobs(){ try { const r=await api.get("/jobs"); setJobs(r.data||[]); } catch { setJobs([]); } }
  async function accept(job){ if(job.id.startsWith("demo")){setToast("Demo job reserved — create an account to accept it");return} try{await api.post(`/jobs/${job.id}/accept`);setToast("Job accepted. It is now on your active board.");loadJobs()}catch(e){setToast(formatError(e))} }
  async function startCheckout(method){ if(!payFor) return; try { const r=await api.post("/payments/checkout",{job_id:payFor.id,origin_url:window.location.origin,payment_method:method}); setPayFor(null); window.location.href=r.data.checkout_url; } catch(e) { setPayFor(null); setToast(formatError(e)); } }
  async function create(data){try{await api.post("/jobs",data);setToast("Job posted. Matching professionals can now see it.");setActive("jobs");loadJobs()}catch(e){setToast(formatError(e))}}
  async function logout(){ try { await api.post("/auth/logout"); } catch {} localStorage.removeItem("taskflow_token"); setUser(null); setJobs([]); setActive("dashboard"); }
  async function refreshUser(){ try { const r = await api.get("/auth/me"); setUser(r.data); } catch {} }

  if(resetToken) return <ResetPasswordScreen token={resetToken} onDone={()=>{ window.history.replaceState(null,"","/"); window.location.reload(); }}/>;
  if(hasOauth || oauthBusy) return <div className="loading-screen" data-testid="oauth-loading">Completing Google sign-in…</div>;
  if(checking) return <div className="loading-screen" data-testid="app-loading">Loading your workspace…</div>;
  if(loc.pathname==="/payment/success") return <PaymentStatus/>;
  if(!user) return <><Landing onAuth={()=>setShowAuth(true)} />{showAuth&&<AuthModal onClose={()=>setShowAuth(false)} onLogin={u=>{setUser(u);setShowAuth(false)}}/>}</>;
  if(user.role==="admin") return <AdminPanel user={user} onLogout={logout} onToast={setToast} toast={toast}/>;

  const worker=user.role==="worker";
  const homeCustomer=user.role==="home_customer";
  const admin=user.role==="admin";
  const activeJobs = jobs.filter(j=>j.status!=="cancelled");
  const openJobs = activeJobs.filter(j=>j.status==="open");
  const inProgressJobs = activeJobs.filter(j=>["accepted","in_progress"].includes(j.status));
  const closedJobs = activeJobs.filter(j=>j.status==="completed");
  const needsRating = (!worker) && closedJobs.find(j => !j.rating);

  function bookPackage(pkg){
    setActive("new");
    setPrefill({title:`${pkg.name} — home IT visit`, description:`Booked the ${pkg.name} package (${pkg.duration_hours} hr). Focus areas: ${pkg.features.join(", ")}.`, category:"Software & cloud", budget:pkg.price, urgency:"This week"});
  }

  const roleLabel = worker ? "IT professional" : homeCustomer ? "Home customer" : "Company workspace";
  const profileSublabel = worker ? "IT Professional" : homeCustomer ? "Home customer" : "Company admin";
  const jobsNavLabel = worker ? "Find jobs" : homeCustomer ? "My bookings" : "My requests";

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><img src={BRAND_LOGO} alt="Dhruva" className="brand-logo" data-testid="brand-logo"/><span>Dhruva <span className="brand-dot">IT</span></span></div>
      <div className="role-chip"><span className="online-dot"/> {roleLabel}</div>
      <nav className="nav-list">
        <button className={active==="dashboard"?"nav-active":""} data-testid="nav-dashboard" onClick={()=>setActive("dashboard")}><BriefcaseBusiness size={18}/> Overview</button>
        {homeCustomer && <button className={active==="packages"?"nav-active":""} data-testid="nav-packages" onClick={()=>setActive("packages")}><Sparkles size={18}/> Support packages</button>}
        {homeCustomer && <button className={active==="technicians"?"nav-active":""} data-testid="nav-technicians" onClick={()=>setActive("technicians")}><MapPin size={18}/> Nearby technicians</button>}
        <button className={active==="jobs"?"nav-active":""} data-testid="nav-jobs" onClick={()=>setActive("jobs")}><Search size={18}/> {jobsNavLabel}</button>
        <button className={active==="history"?"nav-active":""} data-testid="nav-history" onClick={()=>setActive("history")}><Clock3 size={18}/> Job history</button>
        <button className={active==="payments"?"nav-active":""} data-testid="nav-payments" onClick={()=>setActive("payments")}><Wallet size={18}/> {t("paymentMethods")}</button>
        <button className={active==="help"?"nav-active":""} data-testid="nav-help" onClick={()=>setActive("help")}><ShieldCheck size={18}/> {t("help")}</button>
        <button className={active==="profile"?"nav-active":""} data-testid="nav-profile" onClick={()=>setActive("profile")}><UserRound size={18}/> {t("profile")}</button>
      </nav>
      <div className="sidebar-bottom">
        <div className="help-box"><p>Need a hand?</p><span>Our support team is online</span><ChevronRight size={16}/></div>
        <button className="user-mini" data-testid="profile-menu-button" onClick={()=>setActive("profile")}><Avatar user={user} size={31}/><span><b>{user.name}</b><small>{profileSublabel}</small></span><ChevronRight size={15}/></button>
      </div>
    </aside>
    <main className="main">
      <header className="topbar">
        <div className="mobile-brand"><img src={BRAND_LOGO} alt="Dhruva" className="brand-logo"/> Dhruva <span className="brand-dot">IT</span></div>
        <div className="topbar-search"><Search size={17}/><input data-testid="global-search-input" value={globalTerm} onChange={e=>setGlobalTerm(e.target.value)} placeholder="Search jobs, skills, locations…"/></div>
        <div className="topbar-actions">
          <select className="lang-select" data-testid="lang-select" value={lang} onChange={e=>setLang(e.target.value)}>
            <option value="en">EN</option>
            <option value="hi">हिंदी</option>
          </select>
          <NotificationBell/>
          <ProfileDropdown user={user} onLogout={logout} onGoProfile={()=>setActive("profile")} onAvatarUpdated={refreshUser} onToast={setToast}/>
        </div>
      </header>
      {toast&&<div className="toast" data-testid="toast-message">{toast}<button data-testid="toast-close" onClick={()=>setToast("")}><X size={15}/></button></div>}
      {active==="dashboard"&&(homeCustomer
        ? <HomeCustomerDashboard user={user} openJobs={openJobs} inProgressJobs={inProgressJobs} closedJobs={closedJobs} onBookPackage={bookPackage} onViewTechnicians={()=>setActive("technicians")} onAccept={accept} onPay={setPayFor} onOpenChat={setChatJob}/>
        : <Dashboard user={user} worker={worker} openJobs={openJobs} inProgressJobs={inProgressJobs} closedJobs={closedJobs} activeJobs={activeJobs} term={globalTerm} setTerm={setGlobalTerm} onNew={()=>setActive("new")} onAccept={accept} onPay={setPayFor} onOpenChat={setChatJob}/>)}
      {active==="packages"&&<PackagesPage onBook={bookPackage}/>}
      {active==="technicians"&&<TechniciansPage userCity={user.city} onToast={setToast}/>}
      {active==="jobs"&&<Jobs worker={worker} jobs={jobs} onAccept={accept} onNew={()=>setActive("new")} onPay={setPayFor} onOpenChat={setChatJob}/>}
      {active==="new"&&<NewJob prefill={prefill} onCreate={(d)=>{setPrefill(null);create(d);}} onCancel={()=>{setPrefill(null);setActive("dashboard");}}/>}
      {active==="history"&&<History jobs={jobs}/>}
      {active==="payments"&&<PaymentMethods onToast={setToast}/>}
      {active==="help"&&<HelpPage/>}
      {active==="profile"&&<Profile user={user} onAvatarUpdated={refreshUser} onToast={setToast}/>}
    </main>
    {showAuth&&<AuthModal onClose={()=>setShowAuth(false)} onLogin={u=>{setUser(u);setShowAuth(false)}}/>}
    {payFor&&<PaymentMethodModal job={payFor} onClose={()=>setPayFor(null)} onConfirm={startCheckout}/>}
    {needsRating && <RatingModal job={needsRating} onClose={()=>loadJobs()} onSubmit={()=>{setToast("Thanks for the feedback!"); loadJobs();}}/>}
    {chatJob && <ChatPanel job={chatJob} user={user} onClose={()=>setChatJob(null)}/>}
  </div>
}

function Avatar({user, size=32}){
  const [broken,setBroken]=useState(false);
  const url = !broken && user?.avatar_path ? avatarUrl(user.avatar_path) : null;
  const style = { width:size, height:size, fontSize: Math.max(10, Math.floor(size*0.38)) };
  if (url) return <img src={url} alt={user.name} className="avatar-img" style={style} onError={()=>setBroken(true)} data-testid="user-avatar-img"/>;
  return <span className="avatar" style={style}>{(user?.name||"?").slice(0,1).toUpperCase()}</span>;
}


function NotificationBell(){
  const [open,setOpen]=useState(false);
  const [items,setItems]=useState([]);
  const [unread,setUnread]=useState(0);
  async function load(){
    try { const r = await api.get("/notifications"); setItems(r.data.items||[]); setUnread(r.data.unread||0); } catch {}
  }
  useEffect(()=>{ load(); const t=setInterval(load, 30000); return ()=>clearInterval(t); },[]);
  useEffect(()=>{
    if(!open) return;
    const close = e => { if(!e.target.closest('[data-dropdown="bell"]')) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  },[open]);
  async function openPanel(){
    setOpen(o=>!o);
    if(!open && unread>0) {
      try { await api.post("/notifications/read-all"); setUnread(0); setItems(items.map(i=>({...i,read:true}))); } catch {}
    }
  }
  return <div className="profile-dd" data-dropdown="bell">
    <button className="icon-button" data-testid="notifications-button" onClick={openPanel} aria-expanded={open}>
      <Bell size={19}/>{unread>0 && <i data-testid="notification-unread-dot"/>}
    </button>
    {open && <div className="profile-menu notif-menu" data-testid="notifications-panel" style={{width:320}}>
      <div className="profile-menu-head" style={{paddingTop:4,paddingBottom:10}}>
        <b style={{fontSize:13}}>Notifications</b>
      </div>
      {!items.length ? <div className="menu-item" style={{color:"#94a3b8",cursor:"default"}} data-testid="no-notifications">You're all caught up.</div>
        : items.slice(0,8).map(n=><div key={n.id} className="menu-item notif-item" data-testid={`notification-${n.id}`} style={{alignItems:"flex-start",flexDirection:"column",gap:3}}>
          <span style={{fontSize:12,color:"#1e293b",fontWeight:600,whiteSpace:"normal",lineHeight:1.4}}>{n.message}</span>
          <small style={{fontSize:10,color:"#94a3b8"}}>{new Date(n.created_at).toLocaleString("en-IN")}</small>
        </div>)}
    </div>}
  </div>
}

function RatingModal({job, onClose, onSubmit}){
  const [rating,setRating]=useState(5);
  const [review,setReview]=useState("");
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  async function submit(){
    setBusy(true); setErr("");
    try { await api.post(`/jobs/${job.id}/rating`, {rating, review}); onSubmit(); }
    catch(e){ setErr(formatError(e)); }
    finally { setBusy(false); }
  }
  return <div className="modal-backdrop">
    <div className="auth-modal payment-modal" data-testid="rating-modal">
      <button className="modal-close" data-testid="rating-close-button" onClick={onClose}><X/></button>
      <span className="brand-mark" style={{background:"#fef3c7",color:"#d97706"}}><Star size={20}/></span>
      <h2>Rate your experience</h2>
      <p>How was <b>{job.worker_name||"the professional"}</b>'s work on "{job.title}"?</p>
      <div className="rating-stars" data-testid="rating-stars">
        {[1,2,3,4,5].map(n=>(
          <button key={n} type="button" className="star-btn" data-testid={`star-${n}`} onClick={()=>setRating(n)}>
            <Star size={30} fill={n<=rating?"#fbbf24":"transparent"} color={n<=rating?"#fbbf24":"#cbd5e1"}/>
          </button>
        ))}
      </div>
      <label style={{display:"block",marginTop:16}}>
        <span style={{fontSize:11,fontWeight:700,color:"#334155",textTransform:"uppercase",letterSpacing:".4px"}}>Optional review</span>
        <textarea data-testid="rating-review-input" value={review} onChange={e=>setReview(e.target.value)} placeholder="Tell others what went well…" style={{width:"100%",minHeight:70,marginTop:6,padding:10,border:"1px solid #e2e8f0",borderRadius:8,fontFamily:"inherit",fontSize:13}}/>
      </label>
      {err && <div className="form-error" data-testid="rating-error">{err}</div>}
      <button className="primary-button auth-submit" data-testid="rating-submit-button" disabled={busy} onClick={submit}>{busy?"Submitting…":"Submit rating"} <ChevronRight size={16}/></button>
    </div>
  </div>
}

function AdminPanel({user,onLogout,onToast,toast}){
  const [tab,setTab]=useState("overview");
  const [stats,setStats]=useState(null);
  const [users,setUsers]=useState([]);
  const [jobs,setJobs]=useState([]);
  const [verifyFor,setVerifyFor]=useState(null);
  async function loadStats(){ try{ const r=await api.get("/admin/stats"); setStats(r.data);} catch(e){ onToast(formatError(e)); } }
  async function loadUsers(role=""){ try{ const r=await api.get(`/admin/users${role?`?role=${role}`:""}`); setUsers(r.data);} catch(e){ onToast(formatError(e)); } }
  async function loadJobs(){ try{ const r=await api.get("/admin/jobs"); setJobs(r.data);} catch(e){ onToast(formatError(e)); } }
  useEffect(()=>{ loadStats(); },[]);
  useEffect(()=>{ if(tab==="users") loadUsers(); if(tab==="jobs") loadJobs(); if(tab==="verifications") loadUsers("worker"); },[tab]);
  async function verifyWorker(action, reason=""){
    if(!verifyFor) return;
    try { await api.post(`/admin/workers/${verifyFor.id}/verify`, {action, reason}); onToast(`Worker ${action==="approve"?"verified":"rejected"}`); setVerifyFor(null); loadUsers("worker"); loadStats(); }
    catch(e){ onToast(formatError(e)); }
  }
  const pendingWorkers = users.filter(u=>u.role==="worker" && u.verification_status==="pending");
  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><img src={BRAND_LOGO} alt="Dhruva" className="brand-logo"/><span>Dhruva <span className="brand-dot">IT</span></span></div>
      <div className="role-chip"><ShieldCheck size={13} style={{marginRight:5,verticalAlign:"-2px"}}/> Admin</div>
      <nav className="nav-list">
        <button className={tab==="overview"?"nav-active":""} data-testid="admin-nav-overview" onClick={()=>setTab("overview")}><BriefcaseBusiness size={18}/> Overview</button>
        <button className={tab==="verifications"?"nav-active":""} data-testid="admin-nav-verifications" onClick={()=>setTab("verifications")}><ShieldCheck size={18}/> Pro verifications</button>
        <button className={tab==="users"?"nav-active":""} data-testid="admin-nav-users" onClick={()=>setTab("users")}><Users size={18}/> All users</button>
        <button className={tab==="jobs"?"nav-active":""} data-testid="admin-nav-jobs" onClick={()=>setTab("jobs")}><Search size={18}/> All jobs</button>
      </nav>
      <div className="sidebar-bottom">
        <button className="user-mini" data-testid="admin-logout-button" onClick={onLogout}><span className="avatar">{user.name[0]}</span><span><b>{user.name}</b><small>Log out</small></span><LogOut size={14}/></button>
      </div>
    </aside>
    <main className="main">
      <header className="topbar">
        <div className="mobile-brand"><img src={BRAND_LOGO} alt="Dhruva" className="brand-logo"/> Admin console</div>
        <div className="topbar-actions"><span className="count-chip" data-testid="admin-badge">ADMIN</span></div>
      </header>
      {toast&&<div className="toast">{toast}</div>}
      {tab==="overview" && <div className="content">
        <div className="page-heading compact"><div><span className="eyebrow">CONTROL CENTRE</span><h1>Platform overview.</h1><p>Live snapshot of users, jobs, and queue depth.</p></div></div>
        {stats && <div className="stat-grid">
          <Stat label="Total users" value={stats.users_total} meta="all roles" tone="indigo" testid="admin-stat-users"/>
          <Stat label="Pros pending" value={stats.workers_pending} meta="awaiting verification" tone="amber" testid="admin-stat-pending"/>
          <Stat label="Open jobs" value={stats.jobs_open} meta="awaiting a pro" tone="mint" testid="admin-stat-open"/>
          <Stat label="In progress" value={stats.jobs_in_progress} meta="active right now" tone="indigo" testid="admin-stat-progress"/>
          <Stat label="Completed" value={stats.jobs_completed} meta="all time" tone="mint" testid="admin-stat-completed"/>
          <Stat label="Total jobs" value={stats.jobs_total} meta="posted on platform" tone="amber" testid="admin-stat-total"/>
        </div>}
      </div>}
      {tab==="verifications" && <div className="content">
        <div className="page-heading compact"><div><span className="eyebrow">VERIFICATIONS</span><h1>Professional verifications.</h1><p>Approve pros to let them accept jobs.</p></div><span className="count-chip" data-testid="pending-count">{pendingWorkers.length} pending</span></div>
        {!pendingWorkers.length ? <div className="empty-inline" data-testid="no-pending-workers">No pending verifications.</div> : <div className="pm-list">
          {pendingWorkers.map(w => <div key={w.id} className="pm-row" data-testid={`pending-worker-${w.id}`}>
            <Avatar user={w} size={44}/>
            <div className="pm-info"><b>{w.name}</b><small>{w.email} · {w.city||"City not set"} · Skills: {(w.skills||[]).join(", ")||"—"}</small></div>
            <button className="small-button" data-testid={`verify-worker-${w.id}`} onClick={()=>setVerifyFor(w)}>Review <ChevronRight size={13}/></button>
          </div>)}
        </div>}
      </div>}
      {tab==="users" && <div className="content">
        <div className="page-heading compact"><div><span className="eyebrow">USERS</span><h1>All users.</h1><p>Everyone registered on the platform.</p></div></div>
        <div className="pm-list" data-testid="admin-users-list">
          {users.map(u => <div key={u.id} className="pm-row" data-testid={`admin-user-${u.id}`}>
            <Avatar user={u} size={38}/>
            <div className="pm-info"><b>{u.name} {u.role==="worker"&&u.is_verified&&<span className="default-tag" style={{background:"#dcfce7",color:"#166534"}}>Verified</span>}</b><small>{u.email} · {u.role} · {u.city||"—"}</small></div>
            <span className="count-chip">{u.jobs_completed||0} jobs</span>
          </div>)}
        </div>
      </div>}
      {tab==="jobs" && <div className="content">
        <div className="page-heading compact"><div><span className="eyebrow">JOBS</span><h1>All jobs.</h1><p>Every request posted on the platform.</p></div></div>
        <div className="pm-list" data-testid="admin-jobs-list">
          {jobs.map(j => <div key={j.id} className="pm-row" data-testid={`admin-job-${j.id}`}>
            <span className={`status-pill ${j.status}`}>{j.status}</span>
            <div className="pm-info"><b>{j.is_urgent && <Flame size={13} style={{color:"#dc2626",marginRight:5,verticalAlign:"-2px"}}/>}{j.title}</b><small>{j.company_name} · {j.city} · {new Date(j.created_at).toLocaleDateString("en-IN")}</small></div>
            <strong>{formatINR(j.budget)}</strong>
          </div>)}
        </div>
      </div>}
      {verifyFor && <div className="modal-backdrop">
        <div className="auth-modal payment-modal" data-testid="verify-modal">
          <button className="modal-close" onClick={()=>setVerifyFor(null)}><X/></button>
          <span className="brand-mark"><ShieldCheck size={20}/></span>
          <h2>Verify {verifyFor.name}</h2>
          <p>{verifyFor.email} · {verifyFor.city||"City not set"}<br/>Skills: {(verifyFor.skills||[]).join(", ")||"—"}</p>
          <div style={{display:"flex",gap:10,marginTop:16}}>
            <button className="primary-button" data-testid="verify-approve-button" style={{flex:1}} onClick={()=>verifyWorker("approve")}>Approve <CheckCircle2 size={16}/></button>
            <button className="quiet-button danger-btn" data-testid="verify-reject-button" style={{flex:1}} onClick={()=>verifyWorker("reject","Documents incomplete")}>Reject</button>
          </div>
        </div>
      </div>}
    </main>
  </div>
}

function ProfileDropdown({user, onLogout, onGoProfile, onAvatarUpdated, onToast}){
  const [open,setOpen]=useState(false);
  const fileRef = useRef(null);
  const [busy,setBusy]=useState(false);
  useEffect(()=>{
    if(!open) return;
    const close = e => { if(!e.target.closest('[data-dropdown="profile"]')) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  },[open]);
  async function onFile(e){
    const file = e.target.files?.[0];
    e.target.value = "";
    if(!file) return;
    if(!file.type.startsWith("image/")) { onToast("Please choose an image file"); return; }
    if(file.size > 5*1024*1024) { onToast("Image is larger than 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" }});
      await onAvatarUpdated();
      onToast("Profile picture updated");
      setOpen(false);
    } catch(err) { onToast(formatError(err)); }
    finally { setBusy(false); }
  }
  return <div className="profile-dd" data-dropdown="profile">
    <button className="top-avatar-btn" data-testid="profile-avatar-button" onClick={()=>setOpen(o=>!o)} aria-expanded={open}>
      <Avatar user={user} size={35}/>
    </button>
    {open && <div className="profile-menu" data-testid="profile-menu">
      <div className="profile-menu-head">
        <Avatar user={user} size={40}/>
        <div><b>{user.name}</b><small>{user.email}</small></div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{display:"none"}} data-testid="avatar-file-input"/>
      <button className="menu-item" data-testid="menu-upload-avatar" disabled={busy} onClick={()=>fileRef.current?.click()}>
        <Camera size={16}/> <span>{busy?"Uploading…":(user.avatar_path?"Change profile picture":"Upload profile picture")}</span>
      </button>
      <button className="menu-item" data-testid="menu-go-profile" onClick={()=>{setOpen(false);onGoProfile()}}>
        <UserRound size={16}/> <span>View profile</span>
      </button>
      <div className="menu-divider"/>
      <button className="menu-item menu-item-danger" data-testid="menu-logout" onClick={()=>{setOpen(false);onLogout()}}>
        <LogOut size={16}/> <span>Log out</span>
      </button>
    </div>}
  </div>
}

function Landing({onAuth}){
  return <div className="landing">
    <div className="landing-nav">
      <div className="brand"><img src={BRAND_LOGO} alt="Dhruva" className="brand-logo"/><span>Dhruva <span className="brand-dot">IT</span></span></div>
      <button className="outline-button" data-testid="landing-login-button" onClick={onAuth}><LogIn size={15} style={{marginRight:6,verticalAlign:"-2px"}}/> Sign in</button>
    </div>
    <section className="landing-hero">
      <div className="hero-copy">
        <span className="eyebrow"><span className="live-dot"/> ON-DEMAND IT SUPPORT</span>
        <h1>Keep work<br/><em>moving forward.</em></h1>
        <p>Trusted local IT professionals, ready when your business needs them. Post a job, find the right expertise, and get back to what matters.</p>
        <button className="primary-button" data-testid="landing-get-started-button" onClick={onAuth}>Get started <ChevronRight size={18}/></button>
        <div className="trust-row"><span><CheckCircle2 size={16}/> Vetted professionals</span><span><ShieldCheck size={16}/> Secure payments</span></div>
      </div>
      <div className="hero-visual">
        <div className="hero-image"/>
        <div className="floating-card match-card"><span className="match-icon"><CheckCircle2 size={18}/></span><div><b>Perfect match found</b><small>Network support · 2.4 km away</small></div></div>
        <div className="floating-card rating-card"><Star size={18} fill="#fbbf24" color="#fbbf24"/><b>4.9</b><small>average rating</small></div>
      </div>
    </section>
    <div className="landing-stats">
      <span><b>2,400+</b><small>jobs completed</small></span>
      <span><b>4.9/5</b><small>average rating</small></span>
      <span><b>Same day</b><small>response times</small></span>
    </div>
  </div>
}

function Dashboard({user,worker,openJobs,inProgressJobs,closedJobs,activeJobs,term,setTerm,onNew,onAccept,onPay}){
  const filterFn = j => `${j.title} ${j.category} ${j.city} ${j.company_name||""}`.toLowerCase().includes((term||"").toLowerCase());
  const openFiltered = openJobs.filter(filterFn);
  const inProgressFiltered = inProgressJobs.filter(filterFn);
  return <div className="content">
    <div className="page-heading">
      <div>
        <span className="eyebrow">{todayString()}</span>
        <h1 data-testid="dashboard-greeting">{greeting()}, {user.name.split(" ")[0]}.</h1>
        <p>{worker?"Find work that fits your skills, schedule, and radius.":"Here's what's happening across your support requests."}</p>
      </div>
      {!worker&&<button className="primary-button" data-testid="post-job-button" onClick={onNew}><Plus size={18}/> Post a new job</button>}
    </div>
    <div className="stat-grid">
      <Stat label="Open Requests" value={openJobs.length} meta={worker?"available near you":"awaiting a professional"} tone="indigo" testid="stat-open-requests"/>
      <Stat label="In Progress" value={inProgressJobs.length} meta="active right now" tone="mint" testid="stat-in-progress"/>
      <Stat label="Closed Tickets" value={closedJobs.length} meta={worker?"completed by you":"resolved successfully"} tone="amber" testid="stat-closed-tickets"/>
    </div>

    {worker && <div className="dashboard-search"><Search size={17}/><input data-testid="dashboard-search-input" value={term} onChange={e=>setTerm(e.target.value)} placeholder="Search by skill, title, or city"/></div>}

    <div className="section-head">
      <div><h2>{worker?"Recommended for you":"Your active requests"}</h2><p>{worker?"Based on your skills and location":"Open requests waiting for a professional"}</p></div>
      <button className="text-button" data-testid="view-all-jobs-button" onClick={()=>document.querySelector('[data-testid="nav-jobs"]').click()}>View all <ChevronRight size={16}/></button>
    </div>
    <div className="job-grid" data-testid="dashboard-open-grid">
      {openFiltered.slice(0,3).map(j=><JobCard key={j.id} job={j} worker={worker} onAccept={onAccept} onPay={onPay}/>)}
    </div>
    {!openFiltered.length&&<EmptyState worker={worker} onNew={onNew} label={term?"No results match your search":"No open requests yet"}/>}

    <div className="section-head" style={{marginTop:"48px"}}>
      <div><h2>In progress</h2><p>{worker?"Jobs you've accepted and are actively working on":"Requests currently being handled by professionals"}</p></div>
      <span className="count-chip" data-testid="in-progress-count">{inProgressJobs.length} active</span>
    </div>
    <div className="job-grid" data-testid="dashboard-in-progress-grid">
      {inProgressFiltered.map(j=><JobCard key={j.id} job={j} worker={worker} onAccept={onAccept} onPay={onPay}/>)}
    </div>
    {!inProgressFiltered.length&&<div className="empty-inline" data-testid="in-progress-empty">Nothing in progress right now.</div>}
  </div>
}

function Stat({label,value,meta,tone,testid}){return <div className={`stat-card ${tone}`} data-testid={testid||`stat-${label.toLowerCase().replaceAll(" ","-")}`}><span>{label}</span><strong>{value}</strong><small>{meta}</small></div>}

function JobCard({job,worker,onAccept,onPay,onOpenChat}){
  const statusLabel = job.status==="open"?"Open":job.status==="in_progress"?"In progress":job.status==="accepted"?"Accepted":job.status;
  const canChat = job.status !== "open" && job.status !== "cancelled" && job.worker_id;
  return <article className={`job-card ${job.is_urgent?"urgent":""}`} data-testid={`job-card-${job.id}`}>
    {job.is_urgent && <span className="urgent-badge" data-testid={`urgent-badge-${job.id}`}><Flame size={12}/> URGENT</span>}
    <div className="job-card-top"><span className={`status-pill ${job.status}`}>{statusLabel}</span><span className="job-price" data-testid={`job-price-${job.id}`}>{formatINR(job.budget)}</span></div>
    <h3>{job.title}</h3>
    <p className="job-company">{job.company_name}</p>
    <div className="job-meta"><span><MapPin size={14}/>{job.city}</span><span><Clock3 size={14}/>{job.urgency}</span></div>
    <div className="job-footer">
      <span className="skill-tag">{job.category}</span>
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        {canChat && onOpenChat && <button className="small-button" data-testid={`chat-job-${job.id}`} onClick={()=>onOpenChat(job)} title="Open chat"><MessageSquare size={13}/></button>}
        {worker&&job.status==="open"?<button className="small-button" data-testid={`accept-job-${job.id}`} onClick={()=>onAccept(job)}>Accept job <ChevronRight size={15}/></button>
        :!worker&&job.status!=="open"?<button className="small-button" data-testid={`pay-job-${job.id}`} onClick={()=>onPay(job)}>Pay securely <ShieldCheck size={14}/></button>
        :<span className="radius"><MapPin size={13}/>{job.radius} km radius</span>}
      </div>
    </div>
  </article>
}

function Jobs({worker,jobs,onAccept,onNew,onPay}){
  const [term,setTerm]=useState("");
  const filtered = jobs.filter(j=>`${j.title} ${j.category} ${j.city}`.toLowerCase().includes(term.toLowerCase()));
  return <div className="content">
    <div className="page-heading compact">
      <div>
        <span className="eyebrow">{worker?"OPEN OPPORTUNITIES":"REQUESTS CENTER"}</span>
        <h1>{worker?"Find your next job.":"Manage your requests."}</h1>
        <p>{worker?"Browse nearby work matched to your expertise.":"Post, track, and pay for the support your team needs."}</p>
      </div>
      {!worker&&<button className="primary-button" data-testid="jobs-post-button" onClick={onNew}><Plus size={18}/> Post a new job</button>}
    </div>
    <div className="filter-row">
      <div className="filter-search"><Search size={17}/><input data-testid="job-search-input" value={term} onChange={e=>setTerm(e.target.value)} placeholder="Search by skill, title, or city"/></div>
      <button className="filter-chip" data-testid="location-filter-button"><MapPin size={15}/> Within 25 km</button>
      <button className="filter-chip" data-testid="category-filter-button">All specialties</button>
    </div>
    <div className="job-grid">{filtered.map(j=><JobCard key={j.id} job={j} worker={worker} onAccept={onAccept} onPay={onPay}/>)}</div>
  </div>
}

function NewJob({onCreate,onCancel,prefill}){
  const [form,setForm]=useState({title:"",description:"",category:"Network support",city:"Bengaluru, KA",address:"",budget:12000,radius:25,urgency:"Today",is_urgent:false,...(prefill||{})});
  const update=e=>setForm({...form,[e.target.name]:e.target.value});
  return <div className="content narrow">
    <div className="page-heading compact"><div><span className="eyebrow">NEW REQUEST</span><h1>Tell us what needs fixing.</h1><p>Share the essentials and we'll surface the right local professionals.</p></div><button className="quiet-button" data-testid="cancel-job-button" onClick={onCancel}>Cancel</button></div>
    <form className="job-form" onSubmit={e=>{e.preventDefault();onCreate({...form,budget:Number(form.budget),radius:Number(form.radius)})}}>
      <label>What do you need help with?<input required name="title" data-testid="job-title-input" value={form.title} onChange={update} placeholder="e.g. Wi-Fi outage across the office"/></label>
      <label>Describe the issue<textarea required name="description" data-testid="job-description-input" value={form.description} onChange={update} placeholder="Add context that helps a professional arrive prepared…"/></label>
      <div className="form-grid">
        <label>Specialty<select name="category" data-testid="job-category-select" value={form.category} onChange={update}><option>Network support</option><option>Hardware</option><option>Security</option><option>Software & cloud</option></select></label>
        <label>City / region<input name="city" data-testid="job-city-input" value={form.city} onChange={update}/></label>
        <label>Budget (₹)<input name="budget" type="number" data-testid="job-budget-input" value={form.budget} onChange={update}/></label>
        <label>Match radius (km)<input name="radius" type="number" data-testid="job-radius-input" value={form.radius} onChange={update}/></label>
      </div>
      <label>Exact location <span className="optional">Optional</span><input name="address" data-testid="job-address-input" value={form.address} onChange={update} placeholder="Street, building, or floor"/></label>
      <label className={`urgent-toggle ${form.is_urgent?"on":""}`} data-testid="urgent-toggle">
        <input type="checkbox" checked={form.is_urgent} onChange={e=>setForm({...form,is_urgent:e.target.checked})} data-testid="urgent-checkbox"/>
        <span className="urgent-icon"><Flame size={16}/></span>
        <span className="urgent-label"><b>URGENT — within 2 hours</b><small>Pushes this job to the top of every pro's list</small></span>
      </label>
      <button className="primary-button form-submit" data-testid="submit-job-button" type="submit">Post request <ChevronRight size={17}/></button>
    </form>
  </div>
}

function History({jobs}){
  return <div className="content">
    <div className="page-heading compact"><div><span className="eyebrow">ARCHIVE</span><h1>Job history.</h1><p>A clear record of every request and outcome.</p></div></div>
    <div className="history-list">{jobs.map(j=><div className="history-row" key={j.id} data-testid={`history-row-${j.id}`}><span className={`history-icon ${j.status}`}><CheckCircle2 size={18}/></span><div><b>{j.title}</b><small>{j.company_name} · {j.city}</small></div><span className={`status-pill ${j.status}`}>{j.status}</span><strong>{formatINR(j.budget)}</strong></div>)}</div>
  </div>
}

function PaymentMethods({onToast}){
  const [items,setItems]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showAdd,setShowAdd]=useState(false);
  const [form,setForm]=useState({type:"card",label:"",last4:"",is_default:false});
  async function load(){ setLoading(true); try{ const r=await api.get("/payments/methods"); setItems(r.data); }catch(e){ onToast(formatError(e)); } finally{ setLoading(false); } }
  useEffect(()=>{ load(); },[]);
  async function submit(e){ e.preventDefault(); try{ await api.post("/payments/methods", { ...form, last4:(form.last4||"").slice(-4) }); setShowAdd(false); setForm({type:"card",label:"",last4:"",is_default:false}); onToast("Payment method saved"); load(); } catch(err){ onToast(formatError(err)); } }
  async function remove(id){ try{ await api.delete(`/payments/methods/${id}`); onToast("Payment method removed"); load(); } catch(err){ onToast(formatError(err)); } }
  const iconFor = t => t==="upi"?<Smartphone size={19}/>:t==="netbanking"?<Landmark size={19}/>:<CreditCard size={19}/>;
  return <div className="content">
    <div className="page-heading compact"><div><span className="eyebrow">BILLING</span><h1>Payment methods.</h1><p>Save cards, UPI IDs, and bank accounts for one-tap checkout.</p></div><button className="primary-button" data-testid="add-payment-method-button" onClick={()=>setShowAdd(true)}><Plus size={17}/> Add method</button></div>
    {loading?<div className="empty-inline">Loading…</div>:!items.length?<div className="empty-inline" data-testid="no-payment-methods">No saved methods yet. Add one to speed up checkout.</div>:<div className="pm-list" data-testid="payment-methods-list">
      {items.map(m=><div key={m.id} className="pm-row" data-testid={`payment-method-${m.id}`}>
        <span className="pay-icon">{iconFor(m.type)}</span>
        <div className="pm-info"><b>{m.label}{m.is_default&&<span className="default-tag">Default</span>}</b><small>{m.type.toUpperCase()}{m.last4?` · •••• ${m.last4}`:""}</small></div>
        <button className="quiet-button danger-btn" data-testid={`delete-payment-method-${m.id}`} onClick={()=>remove(m.id)}><Trash2 size={14}/></button>
      </div>)}
    </div>}
    {showAdd&&<div className="modal-backdrop"><div className="auth-modal payment-modal" data-testid="add-payment-method-modal">
      <button className="modal-close" onClick={()=>setShowAdd(false)}><X/></button>
      <span className="brand-mark"><Wallet size={20}/></span>
      <h2>Add payment method</h2>
      <p>Store it once, use it for every future request.</p>
      <form onSubmit={submit}>
        <label>Type<select name="type" data-testid="pm-type-select" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}><option value="card">Credit / Debit Card</option><option value="upi">UPI</option><option value="netbanking">Net Banking</option></select></label>
        <label>Label<input required data-testid="pm-label-input" value={form.label} onChange={e=>setForm({...form,label:e.target.value})} placeholder={form.type==="upi"?"work@okhdfcbank":form.type==="netbanking"?"HDFC Bank":"HDFC Platinum Card"}/></label>
        {form.type!=="netbanking"&&<label>Last 4 digits <span className="optional">Optional</span><input maxLength="4" data-testid="pm-last4-input" value={form.last4} onChange={e=>setForm({...form,last4:e.target.value.replace(/\D/g,"")})} placeholder="1234"/></label>}
        <label className="check-row"><input type="checkbox" data-testid="pm-default-input" checked={form.is_default} onChange={e=>setForm({...form,is_default:e.target.checked})}/> Make this my default method</label>
        <button className="primary-button auth-submit" data-testid="pm-submit-button" type="submit">Save method <ChevronRight size={16}/></button>
      </form>
    </div></div>}
  </div>
}

function Profile({user,onAvatarUpdated,onToast}){
  const fileRef = useRef(null);
  const [busy,setBusy]=useState(false);
  async function onFile(e){
    const file = e.target.files?.[0];
    e.target.value = "";
    if(!file) return;
    if(file.size > 5*1024*1024) { onToast("Image is larger than 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" }});
      await onAvatarUpdated();
      onToast("Profile picture updated");
    } catch(err) { onToast(formatError(err)); }
    finally { setBusy(false); }
  }
  return <div className="content">
    <div className="page-heading compact"><div><span className="eyebrow">ACCOUNT</span><h1>Your profile.</h1><p>Keep your details current so every match starts with trust.</p></div></div>
    <div className="profile-layout">
      <div className="profile-card">
        <div className="profile-avatar-wrap">
          <Avatar user={user} size={75}/>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{display:"none"}} data-testid="profile-avatar-file"/>
          <button className="camera-btn" data-testid="profile-change-avatar" disabled={busy} onClick={()=>fileRef.current?.click()} title="Change picture"><Camera size={14}/></button>
        </div>
        <h2>{user.name}</h2>
        <p>{user.role==="worker"?"IT Professional":"Company admin"}</p>
        <div className="profile-rating"><Star size={16} fill="#fbbf24" color="#fbbf24"/> {user.rating||5.0} <span>· {user.jobs_completed||0} jobs completed</span></div>
      </div>
      <div className="details-card"><h3>Profile details</h3><label>Full name<input data-testid="profile-name-input" value={user.name} readOnly/></label><label>Email address<input data-testid="profile-email-input" value={user.email} readOnly/></label><label>Primary location<input data-testid="profile-city-input" value={user.city||"Bengaluru, KA"} readOnly/></label></div>
    </div>
  </div>
}

function EmptyState({worker,onNew,label}){return <div className="empty-state"><BriefcaseBusiness size={28}/><h3>{label||"No active requests yet"}</h3>{!worker&&onNew&&<button className="primary-button" data-testid="empty-post-job-button" onClick={onNew}>Post your first job</button>}</div>}

function HomeCustomerDashboard({user,openJobs,inProgressJobs,closedJobs,onBookPackage,onViewTechnicians,onAccept,onPay}){
  const [packages,setPackages]=useState([]);
  useEffect(()=>{ api.get("/packages").then(r=>setPackages(r.data)).catch(()=>{}); },[]);
  return <div className="content">
    <div className="page-heading">
      <div>
        <span className="eyebrow">{todayString()}</span>
        <h1 data-testid="dashboard-greeting">{greeting()}, {user.name.split(" ")[0]}.</h1>
        <p>Book a technician for your home in minutes — flat prices, verified pros.</p>
      </div>
      <button className="primary-button" data-testid="book-technician-button" onClick={onViewTechnicians}><Sparkles size={17}/> Browse technicians</button>
    </div>
    <div className="stat-grid">
      <Stat label="My Bookings" value={openJobs.length} meta="waiting for a match" tone="indigo" testid="stat-my-bookings"/>
      <Stat label="In Progress" value={inProgressJobs.length} meta="technician on it" tone="mint" testid="stat-hc-in-progress"/>
      <Stat label="Closed Tickets" value={closedJobs.length} meta="completed visits" tone="amber" testid="stat-hc-closed"/>
    </div>
    <div className="section-head">
      <div><h2>Prepaid support packages</h2><p>Fixed prices, no surprises. Pick a package and we'll match a nearby pro.</p></div>
      <span className="count-chip">{packages.length} plans</span>
    </div>
    <div className="package-grid" data-testid="package-grid">
      {packages.map(p => (
        <article key={p.id} className={`package-card ${p.recommended?"recommended":""}`} data-testid={`package-${p.id}`}>
          {p.recommended&&<span className="reco-badge">Most popular</span>}
          <h3>{p.name}</h3>
          <div className="package-price">{formatINR(p.price)}<small>· {p.duration_hours} hr visit</small></div>
          <p className="package-desc">{p.description}</p>
          <ul className="package-features">{p.features.map(f=><li key={f}><CheckCircle2 size={14}/> {f}</li>)}</ul>
          <button className="primary-button package-cta" data-testid={`book-package-${p.id}`} onClick={()=>onBookPackage(p)}>Book this package <ChevronRight size={16}/></button>
        </article>
      ))}
    </div>
    {(inProgressJobs.length>0 || openJobs.length>0) && <>
      <div className="section-head" style={{marginTop:"48px"}}>
        <div><h2>Your active visits</h2><p>Bookings currently on the way or being worked on.</p></div>
      </div>
      <div className="job-grid" data-testid="hc-active-grid">
        {[...openJobs, ...inProgressJobs].slice(0,6).map(j=><JobCard key={j.id} job={j} worker={false} onAccept={onAccept} onPay={onPay}/>)}
      </div>
    </>}
  </div>
}

function PackagesPage({onBook}){
  const [packages,setPackages]=useState([]);
  useEffect(()=>{ api.get("/packages").then(r=>setPackages(r.data)).catch(()=>{}); },[]);
  return <div className="content">
    <div className="page-heading compact"><div><span className="eyebrow">SUPPORT PACKAGES</span><h1>Flat-price home IT visits.</h1><p>Choose a package. We match a verified nearby technician.</p></div></div>
    <div className="package-grid">
      {packages.map(p => (
        <article key={p.id} className={`package-card ${p.recommended?"recommended":""}`} data-testid={`package-full-${p.id}`}>
          {p.recommended&&<span className="reco-badge">Most popular</span>}
          <h3>{p.name}</h3>
          <div className="package-price">{formatINR(p.price)}<small>· {p.duration_hours} hr visit</small></div>
          <p className="package-desc">{p.description}</p>
          <ul className="package-features">{p.features.map(f=><li key={f}><CheckCircle2 size={14}/> {f}</li>)}</ul>
          <button className="primary-button package-cta" data-testid={`book-package-full-${p.id}`} onClick={()=>onBook(p)}>Book this package <ChevronRight size={16}/></button>
        </article>
      ))}
    </div>
  </div>
}

function TechniciansPage({userCity,onToast}){
  const [items,setItems]=useState([]);
  const [loading,setLoading]=useState(true);
  const [city,setCity]=useState(userCity||"");
  async function load(){ setLoading(true); try{ const r=await api.get(`/technicians/nearby?city=${encodeURIComponent(city||"")}`); setItems(r.data);} catch(e){ onToast(formatError(e)); } finally{ setLoading(false); } }
  useEffect(()=>{ load(); },[]);
  return <div className="content">
    <div className="page-heading compact"><div><span className="eyebrow">DIRECTORY</span><h1>Nearby technicians.</h1><p>Verified IT professionals ranked by rating.</p></div></div>
    <div className="filter-row">
      <div className="filter-search"><MapPin size={17}/><input data-testid="technician-city-input" value={city} onChange={e=>setCity(e.target.value)} placeholder="Filter by city (e.g. Bengaluru)"/></div>
      <button className="filter-chip" data-testid="technician-search-button" onClick={load}><Search size={15}/> Search</button>
    </div>
    {loading ? <div className="empty-inline">Finding pros near you…</div>
      : !items.length ? <div className="empty-inline" data-testid="no-technicians">No technicians listed{city?` in ${city}`:""} yet.</div>
      : <div className="pm-list" data-testid="technicians-list">
      {items.map(w => <div key={w.id} className="pm-row technician-row" data-testid={`technician-${w.id}`}>
        <Avatar user={w} size={44}/>
        <div className="pm-info">
          <b>{w.name}</b>
          <small>{w.city||"City not listed"} · {w.skills.slice(0,3).join(" · ")||"General IT support"}</small>
        </div>
        <div className="tech-rating"><Star size={14} fill="#fbbf24" color="#fbbf24"/> <b>{(w.rating||5).toFixed(1)}</b> <small>· {w.jobs_completed||0} jobs</small></div>
      </div>)}
    </div>}
  </div>
}


function AuthModal({onClose,onLogin}){
  const [role,setRole]=useState(null); // null = show role picker; else "company"|"worker"|"home_customer"
  const [mode,setMode]=useState("login");
  const [form,setForm]=useState({name:"",email:"",password:"",city:"Bengaluru, KA"});
  const [error,setError]=useState("");
  const [busy,setBusy]=useState(false);
  const [showForgot,setShowForgot]=useState(false);
  const update=e=>setForm({...form,[e.target.name]:e.target.value});
  const roles = [
    { id:"company", label:"Business Login", meta:"For companies posting IT requests", testid:"role-business" },
    { id:"worker", label:"IT Professional Login", meta:"For technicians accepting jobs", testid:"role-professional" },
    { id:"home_customer", label:"Home Customer Login", meta:"For individuals needing home IT help", testid:"role-home-customer" }
  ];
  async function submit(e){
    e.preventDefault(); setBusy(true); setError("");
    try {
      const r = await api.post(`/auth/${mode}`, mode==="login" ? {email:form.email,password:form.password} : {...form, role});
      localStorage.setItem("taskflow_token", r.data.access_token||"");
      onLogin(r.data);
    } catch(err) { setError(formatError(err)); }
    finally { setBusy(false); }
  }
  function googleSignIn(){
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    if (role) localStorage.setItem("pending_google_role", role);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }
  return <div className="modal-backdrop dark-backdrop">
    <div className="auth-modal navy-modal" data-testid="auth-modal">
      <button className="modal-close light-close" data-testid="auth-close-button" onClick={onClose}><X/></button>
      <div className="navy-brand"><img src={BRAND_LOGO} alt="Dharuvaa" className="modal-logo"/><h1 data-testid="brand-heading">Dharuvaa IT Services</h1></div>
      {!role ? <>
        <p className="navy-sub">Choose how you'd like to sign in.</p>
        <div className="role-buttons">
          {roles.map(r => (
            <button key={r.id} type="button" className="role-btn" data-testid={r.testid} onClick={()=>setRole(r.id)}>
              <span className="role-btn-title">{r.label}</span>
              <span className="role-btn-meta">{r.meta}</span>
              <ChevronRight size={18}/>
            </button>
          ))}
        </div>
        <div className="or-divider"><span>or</span></div>
        <button type="button" className="google-btn" data-testid="google-signin-button" onClick={googleSignIn}>
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.1 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9C29.1 35.5 26.7 36 24 36c-5.4 0-9.9-3.4-11.6-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6v.1l6 4.9C36.8 41.4 44 36 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
          Continue with Google
        </button>
        <button type="button" className="forgot-link" data-testid="forgot-password-link" onClick={()=>setShowForgot(true)}>Forgot Password?</button>
      </> : <>
        <button type="button" className="navy-back" data-testid="role-back-button" onClick={()=>{setRole(null);setError("");}}>← Back</button>
        <p className="navy-sub">{mode==="login"?`Signing in as ${roles.find(x=>x.id===role).label}.`:`Create your ${roles.find(x=>x.id===role).label} account.`}</p>
        <form onSubmit={submit} className="navy-form">
          {mode==="register"&&<label>Your name<input required name="name" data-testid="auth-name-input" value={form.name} onChange={update} placeholder="Alex Morgan"/></label>}
          <label>Email<input required type="email" name="email" data-testid="auth-email-input" value={form.email} onChange={update} placeholder="you@company.com"/></label>
          <label>Password<input required minLength="6" type="password" name="password" data-testid="auth-password-input" value={form.password} onChange={update} placeholder="••••••••"/></label>
          {mode==="register"&&<label>City / region<input name="city" data-testid="auth-city-input" value={form.city} onChange={update}/></label>}
          {error&&<div className="form-error" data-testid="auth-error">{error}</div>}
          <button className="navy-submit" data-testid="auth-submit-button" disabled={busy}>{busy?"Working…":mode==="login"?"Sign in":"Create account"} <ChevronRight size={16}/></button>
        </form>
        <button type="button" className="google-btn compact" data-testid="google-signin-button" onClick={googleSignIn}>
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.1 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9C29.1 35.5 26.7 36 24 36c-5.4 0-9.9-3.4-11.6-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6v.1l6 4.9C36.8 41.4 44 36 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
          Continue with Google
        </button>
        <div className="navy-links">
          <button type="button" className="forgot-link" data-testid="forgot-password-link" onClick={()=>setShowForgot(true)}>Forgot Password?</button>
          <button type="button" className="switch-auth-link" data-testid="auth-switch-button" onClick={()=>{setMode(mode==="login"?"register":"login");setError("");}}>{mode==="login"?"Create an account":"Already have an account? Sign in"}</button>
        </div>
      </>}
      {showForgot&&<ForgotPasswordModal onClose={()=>setShowForgot(false)}/>}
    </div>
  </div>
}

function ForgotPasswordModal({onClose}){
  const [email,setEmail]=useState("");
  const [busy,setBusy]=useState(false);
  const [sent,setSent]=useState(false);
  const [error,setError]=useState("");
  async function submit(e){
    e.preventDefault(); setBusy(true); setError("");
    try { await api.post("/auth/forgot-password", {email}); setSent(true); }
    catch(err){ setError(formatError(err)); }
    finally{ setBusy(false); }
  }
  return <div className="modal-backdrop dark-backdrop" onClick={onClose}>
    <div className="auth-modal navy-modal small" data-testid="forgot-password-modal" onClick={e=>e.stopPropagation()}>
      <button className="modal-close light-close" onClick={onClose}><X/></button>
      <h2>Reset your password</h2>
      {sent ? <>
        <p className="navy-sub">If an account with <b>{email}</b> exists, we've sent a reset link. Check your inbox (and spam).</p>
        <button className="navy-submit" data-testid="forgot-close-button" onClick={onClose}>Got it</button>
      </> : <>
        <p className="navy-sub">Enter your email and we'll send you a link to set a new password.</p>
        <form onSubmit={submit} className="navy-form">
          <label>Email<input required type="email" data-testid="forgot-email-input" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" autoFocus/></label>
          {error&&<div className="form-error">{error}</div>}
          <button className="navy-submit" data-testid="forgot-submit-button" disabled={busy}>{busy?"Sending…":"Send reset link"}</button>
        </form>
      </>}
    </div>
  </div>
}

function ResetPasswordScreen({token,onDone}){
  const [password,setPassword]=useState("");
  const [confirm,setConfirm]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [ok,setOk]=useState(false);
  async function submit(e){
    e.preventDefault();
    if(password.length<6){setError("Password must be at least 6 characters");return}
    if(password!==confirm){setError("Passwords do not match");return}
    setBusy(true); setError("");
    try { await api.post("/auth/reset-password", {token, new_password: password}); setOk(true); }
    catch(err){ setError(formatError(err)); }
    finally{ setBusy(false); }
  }
  return <div className="navy-page">
    <div className="auth-modal navy-modal small" data-testid="reset-password-screen">
      <div className="navy-brand"><img src={BRAND_LOGO} alt="Dharuvaa" className="modal-logo"/><h1>Dharuvaa IT Services</h1></div>
      {ok ? <>
        <p className="navy-sub">Your password has been updated. You can now sign in with the new password.</p>
        <button className="navy-submit" data-testid="reset-done-button" onClick={onDone}>Continue to sign in</button>
      </> : <>
        <h2 style={{color:"#fff",marginTop:14}}>Set a new password</h2>
        <p className="navy-sub">Choose something at least 6 characters long.</p>
        <form onSubmit={submit} className="navy-form">
          <label>New password<input required type="password" minLength="6" data-testid="reset-password-input" value={password} onChange={e=>setPassword(e.target.value)} autoFocus/></label>
          <label>Confirm new password<input required type="password" minLength="6" data-testid="reset-confirm-input" value={confirm} onChange={e=>setConfirm(e.target.value)}/></label>
          {error&&<div className="form-error">{error}</div>}
          <button className="navy-submit" data-testid="reset-submit-button" disabled={busy}>{busy?"Updating…":"Update password"} <ChevronRight size={16}/></button>
        </form>
      </>}
    </div>
  </div>
}

function PaymentMethodModal({job,onClose,onConfirm}){
  const [method,setMethod]=useState("card");
  const options = [
    { id:"card", label:"Credit / Debit Card", meta:"Visa, Mastercard, RuPay, Amex", Icon:CreditCard },
    { id:"upi", label:"UPI", meta:"Google Pay, PhonePe, Paytm, BHIM", Icon:Smartphone },
    { id:"netbanking", label:"Net Banking", meta:"All major Indian banks", Icon:Landmark }
  ];
  return <div className="modal-backdrop">
    <div className="auth-modal payment-modal" data-testid="payment-method-modal">
      <button className="modal-close" data-testid="payment-close-button" onClick={onClose}><X/></button>
      <span className="brand-mark"><Wallet size={20}/></span>
      <h2>Choose payment method</h2>
      <p>Paying <b>{formatINR(job.budget)}</b> for “{job.title}”.</p>
      <div className="pay-options">
        {options.map(({id,label,meta,Icon})=>(
          <button key={id} type="button" className={`pay-option ${method===id?"selected":""}`} data-testid={`payment-option-${id}`} onClick={()=>setMethod(id)}>
            <span className="pay-icon"><Icon size={19}/></span>
            <span className="pay-label"><b>{label}</b><small>{meta}</small></span>
            <span className={`pay-radio ${method===id?"checked":""}`}/>
          </button>
        ))}
      </div>
      <button className="primary-button auth-submit" data-testid="payment-confirm-button" onClick={()=>onConfirm(method)}>Continue to secure checkout <ChevronRight size={17}/></button>
    </div>
  </div>
}

function PaymentStatus(){
  const p=new URLSearchParams(useLocation().search);
  return <div className="content"><div className="success-panel"><CheckCircle2 size={42}/><span className="eyebrow">PAYMENT CONFIRMED</span><h1>You're all set.</h1><p>Your support request is funded and ready to move forward.</p><button className="primary-button" data-testid="payment-done-button" onClick={()=>window.location.href="/"}>Back to workspace</button><small data-testid="payment-session-id">Session {p.get("session_id")||"test"}</small></div></div>
}

export default App;
